import apiUtils from '../utils/apiUtils';
import { TryOnSession, TryOnSessionStatus } from '../types';

export interface GenerateTryOnRequest {
  outfitId: string;
}

export interface GenerateTryOnResponse {
  sessionId: string;
  status: 'processing';
  estimatedTime: number; // seconds
}

export interface TryOnStatusResponse {
  sessionId: string;
  status: TryOnSessionStatus;
  resultImageUrl?: string;
  errorMessage?: string;
  progress?: number; // 0-100
}

export class TryOnService {
  /**
   * Initiate a try-on session for an outfit
   */
  static async generateTryOn(outfitId: string): Promise<GenerateTryOnResponse> {
    try {
      const response = await apiUtils.post<GenerateTryOnResponse>('/try-on/generate', {
        outfitId,
      });

      return response;
    } catch (error: any) {
      console.error('Failed to generate try-on:', error);
      throw new Error(error.response?.data?.message || 'Failed to generate try-on');
    }
  }

  /**
   * Get the status of a try-on session
   */
  static async getTryOnStatus(sessionId: string): Promise<TryOnStatusResponse> {
    try {
      const response = await apiUtils.get<TryOnStatusResponse>(`/try-on/status/${sessionId}`);
      return response;
    } catch (error: any) {
      console.error('Failed to get try-on status:', error);
      throw new Error(error.response?.data?.message || 'Failed to get try-on status');
    }
  }

  /**
   * Poll for try-on completion with more frequent updates
   */
  static async pollTryOnStatus(
    sessionId: string,
    onProgress?: (progress: number) => void,
    maxAttempts: number = 60,
    abortSignal?: AbortSignal
  ): Promise<TryOnStatusResponse> {
    let attempts = 0;
    let delay = 2000; // Start with 2 seconds for more frequent updates

    while (attempts < maxAttempts) {
      // Check if polling was aborted
      if (abortSignal?.aborted) {
        throw new Error('Try-on polling aborted');
      }

      try {
        const status = await this.getTryOnStatus(sessionId);
        
        // Always call onProgress with current progress
        if (onProgress) {
          const currentProgress = status.progress !== undefined ? status.progress : 0;
          onProgress(currentProgress);
        }

        // Stop polling on terminal states
        if (
          status.status === TryOnSessionStatus.COMPLETED || 
          status.status === TryOnSessionStatus.FAILED ||
          status.status === TryOnSessionStatus.CANCELLED
        ) {
          // Ensure progress is 100% on completion
          if (status.status === TryOnSessionStatus.COMPLETED && onProgress) {
            onProgress(100);
          }
          return status;
        }

        // Wait before next poll - shorter delay for more frequent updates
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Check again after delay
        if (abortSignal?.aborted) {
          throw new Error('Try-on polling aborted');
        }
        
        // Keep delay consistent at 2 seconds for real-time updates
        delay = 2000;
        attempts++;
      } catch (error) {
        // If aborted, don't retry
        if (abortSignal?.aborted) {
          throw new Error('Try-on polling aborted');
        }
        
        console.error('Error polling try-on status:', error);
        attempts++;
        
        if (attempts >= maxAttempts) {
          throw new Error('Try-on polling timeout');
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw new Error('Try-on processing timeout');
  }

  /**
   * Cancel a try-on session
   */
  static async cancelTryOn(sessionId: string): Promise<void> {
    try {
      await apiUtils.post(`/try-on/cancel/${sessionId}`, {});
    } catch (error: any) {
      console.error('Failed to cancel try-on:', error);
      throw new Error(error.response?.data?.message || 'Failed to cancel try-on');
    }
  }

  /**
   * Check if user has a profile picture for try-on
   */
  static async hasProfilePicture(): Promise<boolean> {
    try {
      const response = await apiUtils.get<{ 
        userId: string;
        hasProfilePicture: boolean; 
        profilePictureUrl?: string;
        uploadedAt?: string;
        status?: string;
      }>('/profile/picture/status');
      return response.hasProfilePicture;
    } catch (error) {
      console.error('Failed to check profile picture status:', error);
      // If the endpoint is not working, assume user needs to upload a profile picture
      // This will show the profile picture prompt, which is safer than assuming they have one
      return false;
    }
  }
}

export default TryOnService;